import {Flex, Tabs, TabsProps, Tag, Typography} from 'antd';
import {Outlet, useLocation, useNavigate, useParams} from 'react-router-dom';
import './InstallationDetailPage.scss';
import {useTranslation} from '../../i18n/i18n';
import {
    dashboardPath,
    graphicsInstallationPath,
    locationInstallationPath,
    sensorInstallationPath,
    summaryInstallationPath,
} from '../../routes';
import {useEffect, useRef, useState} from 'react';
import {installationApi} from '../../api/installation.api';
import {installationStateApi} from '../../api/installation-state.api';
import {installationStateLabel} from '../../utils/mapTranslationEnums';
import useTenantStore from '../../stores/tenant.store';
import {colors} from '../../theme/colors';

const { Title, Text } = Typography;

const InstallationDetailPage = () => {
  const { installationUuid } = useParams<{ installationLocationUuid: string; installationUuid: string }>();
  const { t } = useTranslation('installation-location', { keyPrefix: 'installationLocationDetail.installation.tabs' });
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname.split('/').pop();
  const [current, setCurrent] = useState(currentPath || summaryInstallationPath);

  const [name, setName] = useState<string>('');
  const [erpId, setErpId] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [statusColor, setStatusColor] = useState<string>('');

  const selectedTenant = useTenantStore((state) => state.selectedTenant);
  const prevSelectedTenantRef = useRef(selectedTenant);
  useEffect(() => {
    if (prevSelectedTenantRef.current === selectedTenant) return;
    prevSelectedTenantRef.current = selectedTenant;
    navigate(`/${dashboardPath}/${locationInstallationPath}`);
  }, [selectedTenant, navigate]);

  useEffect(() => {
    if (!installationUuid) return;

    const fetchInstallation = async () => {
      try {
        const inst = await installationApi.getByUuid(installationUuid);
        setName(inst.name);
        setErpId(inst.erpId);
        if (inst.installationStateUuid) {
          const state = await installationStateApi.getByUuid(inst.installationStateUuid);
          setStatus(installationStateLabel(state));
          setStatusColor(state?.color ?? '');
        } else {
          setStatus(installationStateLabel(null));
          setStatusColor(colors.NO_STATE_COLOR);
        }
      } catch (error) {
        console.error('Failed to fetch installation:', error);
      }
    };

    fetchInstallation();
  }, [installationUuid]);

  const navItems: TabsProps['items'] = [
    { key: summaryInstallationPath, label: t('summary') },
    { key: sensorInstallationPath, label: t('sensors') },
    { key: graphicsInstallationPath, label: t('graphic') },
  ];

  const onClick = (key: string) => {
    setCurrent(key);
    const newPath = location.pathname.split('/').slice(0, -1).join('/');
    navigate(`${newPath}/${key}`);
  };

  return (
    <Flex vertical>
      <br />
      <div className={'InstallationDetailPage__header'}>
        <div className={'InstallationDetailPage__header__title'}>
          <Title level={1} className={'InstallationDetailPage__title'}>
            {name || installationUuid}
          </Title>
        </div>
        <Tag className={'InstallationDetailPage__tag'} color={statusColor} variant={'outlined'}>
          <div className={'InstallationDetailPage__tag__point'} style={{ backgroundColor: statusColor }} />
          <Text className={'InstallationDetailPage__tag__text'}>{status}</Text>
        </Tag>
      </div>
      <Text className={'InstallationDetailPage__subtitle'}>{erpId}</Text>
      <br />
      <Tabs items={navItems} activeKey={current} onChange={onClick} />
      <Outlet />
    </Flex>
  );
};

export default InstallationDetailPage;
